import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import Icon from '../../common/Icon'
import { useTranslation } from 'react-i18next'
import Select from '../../common/Select.tsx'
import { AcceptedMediaFileExtensions } from '@/features/media-file/domain/models/media-file.ts'
import getCategories from '@/features/media-file/domain/use-cases/get-categories.ts'
import type { MediaCategory } from '@/features/media-file/data/dto/media-category.ts'
import type { UUID } from '@/features/shared/data/string.types.ts'

interface MediaFilesListFiltersProps {
  setCategory: (category: UUID | undefined) => void
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
  initialCategory?: UUID | undefined
}

const MediaFilesListFilters: React.FC<MediaFilesListFiltersProps> = ({
  setCategory,
  handleFileUpload,
  initialCategory,
}) => {
  const { t } = useTranslation()
  const [categories, setCategories] = useState<MediaCategory[]>([])
  useEffect(() => {
    getCategories().then(setCategories)
  }, [])

  const options = [
    {
      label: 'all',
      value: undefined,
    },
    ...categories.map((category) => ({
      label: category.name.replace(/\s+/g, '').toLocaleLowerCase(),
      value: category.categoryId,
    })),
  ]

  const defaultOption = useMemo(() => {
    if (!initialCategory) return undefined
    const match = categories.find((c) => c.categoryId === initialCategory)
    if (!match) return undefined
    return {
      label: match.name.replace(/\s+/g, '').toLocaleLowerCase(),
      value: match.categoryId,
    }
  }, [initialCategory, categories])

  return (
    <div className="inline-flex w-full gap-2 bg-zinc-900">
      <Select
        filterNameKey="ls_filter_buttons_category"
        options={options}
        onChange={(value) => setCategory(value as UUID)}
        defaultOption={defaultOption}
      />
      <label className="inline-flex h-9 w-full cursor-pointer items-center justify-start gap-x-1.5 rounded-[4px] border border-zinc-500 px-4 text-sm font-semibold text-white shadow-sm hover:bg-white/10">
        <Icon name="upload" width={16} />
        {t('ls_filter_buttons_upload_file')}
        <input
          type="file"
          className="hidden"
          onChange={handleFileUpload}
          accept={AcceptedMediaFileExtensions.join(',')}
        />
      </label>
    </div>
  )
}

export default MediaFilesListFilters
